import React from "react";
import styled from "@emotion/styled";
import {
  FONT_SIZE_MEDIUM,
  BORDER_RADIUS_DEFAULT,
  COLOR_TERTIARY,
  COLOR_PRIMARY,
  FONT_SIZE_SMALL,
  COLOR_ERROR,
  PLACEHOLDER_TEXT_COLOR,
  HEADER_FONT_FAMILY,
  DISABLED_INPUT_FIELD_COLOR,
  COLOR_INPUT_DEFAULT_BORDER,
  COLOR_INPUT_BORDER_FOCUSED,
  COLOR_INPUT_BORDER_HOVER,
} from "../theme";
import { ParagraphHeaderSmall } from "../typography/Paragraph";

const TextAreaWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledTextarea = styled.textarea<{
  hasError?: boolean;
  disabled?: boolean;
}>`
  min-height: 120px;
  padding: 16px;
  border-radius: ${BORDER_RADIUS_DEFAULT};
  font-size: ${FONT_SIZE_MEDIUM};
  font-family: ${HEADER_FONT_FAMILY};
  background-color: ${(props) =>
    props.disabled ? DISABLED_INPUT_FIELD_COLOR : COLOR_TERTIARY};
  outline: none;
  border: ${({ hasError }) =>
    hasError
      ? `1px solid ${COLOR_ERROR}`
      : `1px solid ${COLOR_INPUT_DEFAULT_BORDER}`};
  color: ${COLOR_PRIMARY};
  resize: vertical;
  line-height: 1.5;
  font-weight: 500;
  &::placeholder {
    color: ${PLACEHOLDER_TEXT_COLOR};
  }
  &:focus {
    ${({ hasError }) =>
      !hasError && `border: 1px solid ${COLOR_INPUT_BORDER_FOCUSED};`}
  }
  &:hover {
    ${({ hasError }) =>
      !hasError && `border: 1px solid ${COLOR_INPUT_BORDER_HOVER};`}
  }
`;

const ErrorText = styled(ParagraphHeaderSmall)`
  color: ${COLOR_ERROR};
  font-size: ${FONT_SIZE_SMALL};
  margin: 0;
`;

export interface TextAreaProps {
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  required?: boolean;
  errorMessage?: string | null;
  disabled?: boolean;
  className?: string;
  rows?: number;
  dataTestId?: string;
}

export const TextArea: React.FC<TextAreaProps> = ({
  placeholder,
  value,
  onChange,
  onBlur,
  className,
  errorMessage,
  required,
  disabled = false,
  rows = 4,
  dataTestId,
}) => {
  return (
    <TextAreaWrapper className={className}>
      <StyledTextarea
        data-ls-disabled
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        hasError={errorMessage != null}
        required={required}
        disabled={disabled}
        rows={rows}
        data-testid={dataTestId ?? "textarea"}
      />
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </TextAreaWrapper>
  );
};
